'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { LMS_ASSIGNMENTS } from '@/lib/mockData';
import { toast } from 'sonner';

const STATUS_STYLES: Record<string, string> = {
  Active: 'bg-accent-50 text-accent-700 border border-accent-200',
  Completed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Past Due': 'bg-red-50 text-red-700 border border-red-200',
};

const PLATFORM_STYLES: Record<string, string> = {
  'Google Classroom': 'text-blue-600',
  Canvas: 'text-red-600',
  Schoology: 'text-green-600',
};

export default function LMSAssignmentsTable() {
  const handleViewInLMS = (platform: string, title: string) => {
    // TODO: Backend integration — GET /api/lms/assignments/{id}/link — returns LMS deep link
    toast.info(`Opening ${title} in ${platform}…`);
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
        <h3 className="font-semibold text-stone-800">LMS Assignments</h3>
        <span className="text-xs text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full">
          {LMS_ASSIGNMENTS.length} total
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100">
              <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                Lesson
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                Platform
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                Class
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                Due
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                Submissions
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {LMS_ASSIGNMENTS.map((assignment, i) => (
              <tr
                key={assignment.id}
                className={`border-b border-stone-50 hover:bg-stone-50/80 transition-colors duration-100 group ${
                  i % 2 === 0 ? '' : 'bg-stone-50/30'
                }`}
              >
                <td className="px-5 py-4">
                  <p className="font-medium text-stone-800 line-clamp-1 max-w-xs">
                    {assignment.lessonTitle}
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">{assignment.assignedDate}</p>
                </td>
                <td className="px-4 py-4">
                  <span className={`text-xs font-semibold ${PLATFORM_STYLES[assignment.platform] || 'text-stone-600'}`}>
                    {assignment.platform}
                  </span>
                </td>
                <td className="px-4 py-4 hidden md:table-cell">
                  <span className="text-xs text-stone-600 line-clamp-1 max-w-[160px]">
                    {assignment.class}
                  </span>
                </td>
                <td className="px-4 py-4 hidden lg:table-cell">
                  <span className={`text-xs font-medium tabular-nums ${assignment.status === 'Past Due' ? 'text-red-600' : 'text-stone-600'}`}>
                    {assignment.dueDate}
                  </span>
                </td>
                <td className="px-4 py-4 hidden lg:table-cell">
                  <div className="flex items-center gap-2">
                    <span className="text-xs tabular-nums text-stone-700 font-medium">
                      {assignment.submissions}
                    </span>
                    <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden w-16">
                      <div
                        className={`h-full rounded-full ${
                          assignment.status === 'Past Due' ? 'bg-red-400' : 'bg-accent-500'
                        }`}
                        style={{
                          width: `${Math.round(
                            (parseInt(assignment.submissions.split('/')[0]) /
                              parseInt(assignment.submissions.split('/')[1])) *
                              100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[assignment.status] || ''}`}>
                    {assignment.status}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <button
                    onClick={() => handleViewInLMS(assignment.platform, assignment.lessonTitle)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-stone-400 hover:text-primary-600 hover:bg-primary-50 transition-all duration-150"
                    aria-label={`View ${assignment.lessonTitle} in ${assignment.platform}`}
                    title="Open in LMS"
                  >
                    <Icon name="ArrowTopRightOnSquareIcon" size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}